import { FC, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Grid,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SaveIcon from '@mui/icons-material/Save'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import AddIcon from '@mui/icons-material/Add'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { useSnackbar } from 'notistack'
import { produtoService } from '@/services/produtoService'
import {
  MaterialComercial,
  PerguntaFrequente,
  ProdutoCategoria,
  ProdutoRequest,
  StatusProduto,
} from '@/types/produto'
import { authStore } from '@/store/authStore'
import {
  CATEGORIAS_ESPECIAIS,
  CATEGORIAS_PRODUTO,
  categoriaRetirada,
  rotuloCategoria,
} from '@/constants/categoriasProduto'
import {
  PADRAO_CODIGO_PRODUTO,
  ROTULOS_STATUS_PRODUTO,
  codigoCanonico,
  rotuloStatusProduto,
} from '@/constants/codigosProduto'
import { mensagemErroProdutoP1, MENSAGENS_ERRO_PRODUTO_P1 } from '@/constants/catalogoProdutoP1'

// Espelha o @Pattern do MaterialComercial no backend. Sem flag `g` — de propósito:
// regex global guarda lastIndex entre chamadas e `test` passaria a alternar resultado.
const URL_HTTP = /^https?:\/\/.+/

/**
 * Validação do código na criação (AN-PP1 §1.3), espelho do servidor só para guiar: vazio →
 * obrigatório; fora do padrão da V13 ou prefixo `X_` (reservado à migração) → inválido.
 * Devolve a mensagem ou `null`.
 */
export const validarCodigoNovo = (codigo: string): string | null => {
  const c = codigo.trim()
  if (!c) return MENSAGENS_ERRO_PRODUTO_P1.CODIGO_OBRIGATORIO
  if (!PADRAO_CODIGO_PRODUTO.test(c) || c.startsWith('X_')) return MENSAGENS_ERRO_PRODUTO_P1.CODIGO_INVALIDO
  return null
}

/** Status oferecidos na criação: RETIRADA nunca (422 `STATUS_INVALIDO`). */
const STATUS_CRIACAO = (Object.keys(ROTULOS_STATUS_PRODUTO) as StatusProduto[]).filter((s) => s !== 'RETIRADA')

const ProdutoFormPage: FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const usuario = authStore((s) => s.usuario)
  const isAdmin = !!usuario?.permissoes?.some((p) => p.toUpperCase() === 'ADMIN')
  const editing = !!id

  // Guard cliente-side (o backend também exige ROLE_ADMIN).
  useEffect(() => {
    if (usuario && !isAdmin) {
      enqueueSnackbar('Apenas administradores podem gerenciar produtos', { variant: 'warning' })
      navigate('/produtos')
    }
  }, [usuario, isAdmin, navigate, enqueueSnackbar])

  const [loading, setLoading] = useState(editing)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  // P1 (AN-PP1 §1.3): código obrigatório na criação e imutável depois; status só na criação
  // (a edição usa "Alterar status", PATCH /status). Taxa/valor/prazo saíram (§6).
  const [codigo, setCodigo] = useState('')
  const [statusInicial, setStatusInicial] = useState<StatusProduto>('SEM_POLITICA_SIMULACAO')
  const [statusAtual, setStatusAtual] = useState<StatusProduto | undefined>(undefined)
  const [nome, setNome] = useState('')
  const [categoria, setCategoria] = useState<ProdutoCategoria>('CAPITAL_GIRO')
  const [descricao, setDescricao] = useState('')
  const [descricaoDetalhada, setDescricaoDetalhada] = useState('')
  // P2 (AN-PP2 §8): faturamento/LTV saíram deste formulário — a regra é a política de
  // elegibilidade versionada (aba Elegibilidade do produto, "Nova versão", só ADMIN).
  const [criterios, setCriterios] = useState<string[]>([])
  const [criterioInput, setCriterioInput] = useState('')
  const [documentacao, setDocumentacao] = useState<string[]>([])
  const [documentacaoInput, setDocumentacaoInput] = useState('')

  // Biblioteca de produtos (PDF do Consultor, "Materiais dos produtos")
  const [perfilCliente, setPerfilCliente] = useState('')
  const [beneficios, setBeneficios] = useState<string[]>([])
  const [beneficioInput, setBeneficioInput] = useState('')
  const [videoUrl, setVideoUrl] = useState('')
  const [materiais, setMateriais] = useState<MaterialComercial[]>([])
  const [materialTitulo, setMaterialTitulo] = useState('')
  const [materialUrl, setMaterialUrl] = useState('')
  const [faq, setFaq] = useState<PerguntaFrequente[]>([])
  const [perguntaInput, setPerguntaInput] = useState('')
  const [respostaInput, setRespostaInput] = useState('')

  // Carrega dados existentes em modo edição
  useEffect(() => {
    if (!editing || !id) return
    let ativo = true
    setLoading(true)
    produtoService
      .buscar(id)
      .then((p) => {
        if (!ativo) return
        setCodigo(p.codigo ?? '')
        setStatusAtual(p.status)
        setNome(p.nome)
        setCategoria(p.categoria)
        setDescricao(p.descricao ?? '')
        setDescricaoDetalhada(p.descricaoDetalhada ?? '')
        setCriterios(p.criteriosElegibilidade ?? [])
        setDocumentacao(p.documentacaoNecessaria ?? [])
        setPerfilCliente(p.perfilCliente ?? '')
        setBeneficios(p.beneficios ?? [])
        setVideoUrl(p.videoUrl ?? '')
        setMateriais(p.materiaisComerciais ?? [])
        setFaq(p.faq ?? [])
      })
      .catch((e) => {
        if (ativo) setErro(e instanceof Error ? e.message : 'Falha ao carregar produto')
      })
      .finally(() => {
        if (ativo) setLoading(false)
      })
    return () => {
      ativo = false
    }
  }, [editing, id])

  const erroCodigo = editing ? null : validarCodigoNovo(codigo)
  const retirado = statusAtual === 'RETIRADA'
  // Código canônico tem categoria fixa (422 CODIGO_CATEGORIA_INCOMPATIVEL); "Outra necessidade" idem.
  const categoriaTravada =
    editing && (codigoCanonico(codigo) || CATEGORIAS_ESPECIAIS.has(categoria))

  const canSubmit = useMemo(() => {
    if (!nome.trim()) return false
    if (!editing && validarCodigoNovo(codigo)) return false
    if (retirado) return false
    return true
  }, [nome, editing, codigo, retirado])

  const addCriterio = () => {
    const v = criterioInput.trim()
    if (!v) return
    setCriterios((prev) => [...prev, v])
    setCriterioInput('')
  }
  const addDocumentacao = () => {
    const v = documentacaoInput.trim()
    if (!v) return
    setDocumentacao((prev) => [...prev, v])
    setDocumentacaoInput('')
  }
  const addBeneficio = () => {
    const v = beneficioInput.trim()
    if (!v) return
    setBeneficios((prev) => [...prev, v])
    setBeneficioInput('')
  }
  // O backend recusa URL fora de http(s); barrar aqui evita a ida ao servidor.
  const materialValido = !!materialTitulo.trim() && URL_HTTP.test(materialUrl.trim())
  const addMaterial = () => {
    if (!materialValido) return
    setMateriais((prev) => [...prev, { titulo: materialTitulo.trim(), url: materialUrl.trim() }])
    setMaterialTitulo('')
    setMaterialUrl('')
  }
  // Pergunta sem resposta é pior que nenhuma FAQ — os dois campos andam juntos.
  const faqValida = !!perguntaInput.trim() && !!respostaInput.trim()
  const addFaq = () => {
    if (!faqValida) return
    setFaq((prev) => [...prev, { pergunta: perguntaInput.trim(), resposta: respostaInput.trim() }])
    setPerguntaInput('')
    setRespostaInput('')
  }

  const handleSubmit = async () => {
    if (!canSubmit) return
    setSalvando(true)
    setErro(null)
    try {
      const payload: ProdutoRequest = {
        // Código e status só no POST: no PUT o código é imutável (não vai) e o status tem endpoint próprio.
        ...(!editing && { codigo: codigo.trim(), status: statusInicial }),
        nome: nome.trim(),
        categoria,
        descricao: descricao.trim() || undefined,
        descricaoDetalhada: descricaoDetalhada.trim() || undefined,
        criteriosElegibilidade: criterios,
        documentacaoNecessaria: documentacao,
        perfilCliente: perfilCliente.trim() || undefined,
        beneficios,
        materiaisComerciais: materiais,
        videoUrl: videoUrl.trim() || undefined,
        faq,
      }

      if (editing && id) await produtoService.atualizar(id, payload)
      else await produtoService.criar(payload)

      enqueueSnackbar(editing ? 'Produto atualizado' : 'Produto criado', { variant: 'success' })
      navigate('/produtos')
    } catch (e: unknown) {
      const msg = mensagemErroProdutoP1(e, 'Falha ao salvar produto')
      setErro(msg)
      enqueueSnackbar(msg, { variant: 'error' })
    } finally {
      setSalvando(false)
    }
  }

  if (!isAdmin) return null

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate('/produtos')}
          sx={{ cursor: 'pointer' }}
        >
          Produtos
        </Link>
        <Typography color="textPrimary">{editing ? 'Editar Produto' : 'Novo Produto'}</Typography>
      </Breadcrumbs>

      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/produtos')}>
          Voltar
        </Button>
        <Typography variant="h4" fontWeight={600}>
          {editing ? 'Editar Produto' : 'Novo Produto'}
        </Typography>
      </Stack>

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      {retirado && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Produto retirado do catálogo: o cadastro fica como histórico e não pode mais ser editado.
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Dados principais" />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={editing ? 8 : 6}>
                  <TextField
                    label="Código"
                    value={codigo}
                    onChange={(e) => setCodigo(e.target.value.toUpperCase())}
                    fullWidth
                    required={!editing}
                    disabled={editing}
                    error={!editing && !!codigo && !!erroCodigo}
                    helperText={
                      editing
                        ? 'O código é imutável — identifica o produto em reservas, simulações e seeds.'
                        : codigo && erroCodigo
                          ? erroCodigo
                          : 'Ex.: WORKING_CAPITAL_CORPORATE. Não muda depois de criado; o nome pode mudar.'
                    }
                    inputProps={{ maxLength: 64, 'aria-label': 'Código', style: { fontFamily: 'monospace' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={editing ? 4 : 6}>
                  {editing ? (
                    <TextField
                      label="Status"
                      value={rotuloStatusProduto(statusAtual)}
                      fullWidth
                      disabled
                      helperText="Alterado pelo ADMIN em “Alterar status”, na página do produto."
                    />
                  ) : (
                    <TextField
                      label="Status inicial"
                      select
                      value={statusInicial}
                      onChange={(e) => setStatusInicial(e.target.value as StatusProduto)}
                      fullWidth
                      helperText="Sem política de simulação até ela ser cadastrada."
                    >
                      {STATUS_CRIACAO.map((s) => (
                        <MenuItem key={s} value={s}>
                          {ROTULOS_STATUS_PRODUTO[s]}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    label="Nome do produto"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Categoria"
                    select
                    value={categoria}
                    onChange={(e) => setCategoria(e.target.value as ProdutoCategoria)}
                    fullWidth
                    required
                    disabled={categoriaTravada}
                    helperText={categoriaTravada ? 'Fixa para o código do catálogo canônico.' : undefined}
                  >
                    {CATEGORIAS_PRODUTO.map((c) => (
                      <MenuItem key={c.value} value={c.value}>
                        {c.label}
                      </MenuItem>
                    ))}
                    {/* "Outra necessidade" (OTHER_NEED, AN-PP1 §3.2): exibida, nunca oferecida. */}
                    {CATEGORIAS_ESPECIAIS.has(categoria) && (
                      <MenuItem value={categoria} disabled>
                        {rotuloCategoria(categoria)}
                      </MenuItem>
                    )}
                    {/* Produto histórico de categoria RETIRADA (Duplicata Escritural): mostra o rótulo,
                        mas a opção não é oferecida — o backend recusa a edição (422 PRODUTO_RETIRADO). */}
                    {categoriaRetirada(categoria) && (
                      <MenuItem value={categoria} disabled>
                        {rotuloCategoria(categoria)} (retirada)
                      </MenuItem>
                    )}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Descrição curta"
                    value={descricao}
                    onChange={(e) => setDescricao(e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Descrição detalhada"
                    value={descricaoDetalhada}
                    onChange={(e) => setDescricaoDetalhada(e.target.value)}
                    fullWidth
                    multiline
                    rows={4}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          {/* P1 (AN-PP1 §6): taxa, valor e prazo demonstrativos saíram. P2 (AN-PP2 §8): faturamento
              e LTV também — a regra de elegibilidade é versionada e cadastrada na aba Elegibilidade. */}
          <Alert severity="info">
            A regra de elegibilidade (faturamento mínimo, faixa de valor, LTV ou Avaliação Originei) é uma política
            versionada: cadastre uma nova versão na aba <strong>Elegibilidade</strong> do produto.
          </Alert>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Critérios de elegibilidade" />
            <CardContent>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <TextField
                  size="small"
                  placeholder="Ex.: Faturamento mínimo de R$ 500k"
                  value={criterioInput}
                  onChange={(e) => setCriterioInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addCriterio()
                    }
                  }}
                  fullWidth
                />
                <Button variant="outlined" startIcon={<AddIcon />} onClick={addCriterio}>
                  Adicionar
                </Button>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {criterios.map((c, i) => (
                  <Chip
                    key={`${c}-${i}`}
                    label={c}
                    onDelete={() => setCriterios((prev) => prev.filter((_, idx) => idx !== i))}
                    sx={{ mb: 1 }}
                  />
                ))}
                {criterios.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Nenhum critério adicionado.
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title="Documentação necessária (descrição)"
              subheader="Texto informativo da biblioteca do produto — não é o que a reserva cobra"
            />
            <CardContent>
              {/* 08/09: o negócio procurou aqui onde marcar obrigatório. A REGRA (o que o
                  consultor precisa enviar, obrigatório/opcional/básico) é o Checklist
                  documental, na página do produto — esta lista é só prosa da biblioteca. */}
              <Alert
                severity="info"
                sx={{ mb: 2 }}
                action={
                  editing ? (
                    <Button color="inherit" size="small" onClick={() => navigate(`/produtos/${id}?aba=elegibilidade`)}>
                      Abrir checklist
                    </Button>
                  ) : undefined
                }
              >
                Os documentos que a reserva <strong>cobra</strong> do consultor — com a marcação de
                obrigatório, opcional e básico — são cadastrados no <strong>Checklist documental</strong>,
                na página do produto (aba Elegibilidade){editing ? '' : ', depois de salvar o produto'}.
              </Alert>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <TextField
                  size="small"
                  placeholder="Ex.: Contrato Social, Balanço Patrimonial"
                  value={documentacaoInput}
                  onChange={(e) => setDocumentacaoInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addDocumentacao()
                    }
                  }}
                  fullWidth
                />
                <Button variant="outlined" startIcon={<AddIcon />} onClick={addDocumentacao}>
                  Adicionar
                </Button>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {documentacao.map((d, i) => (
                  <Chip
                    key={`${d}-${i}`}
                    label={d}
                    onDelete={() => setDocumentacao((prev) => prev.filter((_, idx) => idx !== i))}
                    sx={{ mb: 1 }}
                  />
                ))}
                {documentacao.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Nenhum documento adicionado.
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Biblioteca do produto"
              subheader="Opcional — o que o consultor vê na aba Biblioteca (perfil de cliente, benefícios, material comercial, vídeo e FAQ)"
            />
            <CardContent>
              <Stack spacing={3}>
                <TextField
                  label="Perfil de cliente"
                  placeholder="Ex.: Indústrias que vendem a prazo para grandes redes"
                  value={perfilCliente}
                  onChange={(e) => setPerfilCliente(e.target.value)}
                  multiline
                  minRows={2}
                  fullWidth
                />

                <Box>
                  <Typography variant="subtitle2" gutterBottom>Benefícios</Typography>
                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <TextField
                      size="small"
                      placeholder="Ex.: Não consome o limite bancário"
                      value={beneficioInput}
                      onChange={(e) => setBeneficioInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          addBeneficio()
                        }
                      }}
                      fullWidth
                    />
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={addBeneficio}>
                      Adicionar
                    </Button>
                  </Stack>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {beneficios.map((b, i) => (
                      <Chip
                        key={`${b}-${i}`}
                        label={b}
                        onDelete={() => setBeneficios((prev) => prev.filter((_, idx) => idx !== i))}
                        sx={{ mb: 1 }}
                      />
                    ))}
                    {beneficios.length === 0 && (
                      <Typography variant="caption" color="text.secondary">
                        Nenhum benefício adicionado.
                      </Typography>
                    )}
                  </Stack>
                </Box>

                <TextField
                  label="Vídeo explicativo (URL)"
                  placeholder="https://youtu.be/..."
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  fullWidth
                  size="small"
                />

                <Box>
                  <Typography variant="subtitle2" gutterBottom>Material comercial</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    Links para as peças (Drive, site). O arquivo não é hospedado aqui.
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
                    <TextField
                      size="small"
                      label="Título"
                      placeholder="One-pager"
                      value={materialTitulo}
                      onChange={(e) => setMaterialTitulo(e.target.value)}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      size="small"
                      label="URL"
                      placeholder="https://..."
                      value={materialUrl}
                      onChange={(e) => setMaterialUrl(e.target.value)}
                      error={!!materialUrl.trim() && !URL_HTTP.test(materialUrl.trim())}
                      helperText={
                        !!materialUrl.trim() && !URL_HTTP.test(materialUrl.trim())
                          ? 'Deve começar com http:// ou https://'
                          : ' '
                      }
                      sx={{ flex: 2 }}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={addMaterial}
                      disabled={!materialValido}
                      sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' }, mt: { sm: 0.5 } }}
                    >
                      Adicionar
                    </Button>
                  </Stack>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {materiais.map((m, i) => (
                      <Chip
                        key={`${m.url}-${i}`}
                        label={m.titulo}
                        icon={<OpenInNewIcon />}
                        // Clicável para o admin conferir o link que acabou de colar.
                        onClick={() => window.open(m.url, '_blank', 'noopener,noreferrer')}
                        onDelete={() => setMateriais((prev) => prev.filter((_, idx) => idx !== i))}
                        sx={{ mb: 1 }}
                      />
                    ))}
                    {materiais.length === 0 && (
                      <Typography variant="caption" color="text.secondary">
                        Nenhum material adicionado.
                      </Typography>
                    )}
                  </Stack>
                </Box>

                <Box>
                  <Typography variant="subtitle2" gutterBottom>Perguntas frequentes</Typography>
                  <Stack spacing={1} sx={{ mb: 2 }}>
                    <TextField
                      size="small"
                      label="Pergunta"
                      value={perguntaInput}
                      onChange={(e) => setPerguntaInput(e.target.value)}
                      fullWidth
                    />
                    <TextField
                      size="small"
                      label="Resposta"
                      value={respostaInput}
                      onChange={(e) => setRespostaInput(e.target.value)}
                      multiline
                      minRows={2}
                      fullWidth
                    />
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={addFaq}
                      disabled={!faqValida}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      Adicionar pergunta
                    </Button>
                  </Stack>
                  <Stack spacing={1}>
                    {faq.map((f, i) => (
                      <Chip
                        key={`${f.pergunta}-${i}`}
                        label={f.pergunta}
                        onDelete={() => setFaq((prev) => prev.filter((_, idx) => idx !== i))}
                        sx={{ alignSelf: 'flex-start', maxWidth: '100%' }}
                      />
                    ))}
                    {faq.length === 0 && (
                      <Typography variant="caption" color="text.secondary">
                        Nenhuma pergunta adicionada.
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/*
          Onda 5 (AN-11 §1.4): o upload do manual saiu daqui — dois caminhos de publicação
          confundiriam qual é o PDF vigente. O informativo é publicado na aba do produto.
        */}
        <Grid item xs={12}>
          <Alert severity="info" icon={<PictureAsPdfIcon />}>
            O PDF informativo é publicado na aba Informativo do produto
            {editing && id ? (
              <>
                {' — '}
                <Link
                  component="button"
                  type="button"
                  onClick={() => navigate(`/produtos/${id}?aba=informativo`)}
                >
                  abrir a aba Informativo
                </Link>
              </>
            ) : (
              ', depois de criar o produto'
            )}
            .
          </Alert>
        </Grid>

        <Grid item xs={12}>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" onClick={() => navigate('/produtos')} disabled={salvando}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              startIcon={salvando ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              onClick={handleSubmit}
              disabled={!canSubmit || salvando}
            >
              {salvando ? 'Salvando…' : editing ? 'Salvar' : 'Criar Produto'}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  )
}

export default ProdutoFormPage
